import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';

export default function CardsPage() {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    fetch('/CMSC471/FP-gallery-2026-tagged.csv')
      .then((res) => res.text())
      .then((text) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          complete: (results) => {
            const cleaned = results.data.map((row) => ({
              ID: row.ID?.trim(),
              title: row.TITLE?.trim(),
              team: row.NAME?.trim(),
              link: row.LINK?.trim(),
              tags: row.TAG?.trim().split(',')
            }));
            setData(shuffleArray(cleaned));
          },
          error: (err) => {
            console.error('Parsing error:', err);
            setError('Failed to parse CSV.');
          },
        });
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setError('Failed to load CSV.');
      });
  }, []);

  function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  if (error) return <div>Error: {error}</div>;

  const STAFF_PICK = 'staff pick';

  const colormaps = {
    [STAFF_PICK]:    '#c9982a',
    'map':           '#4f95d8',
    'analysis':      '#8a6cca',
    'cute':          '#e07aab',
    'creative':      '#e8854a',
    'animation':     '#d4aa40',
    'cool stuff':    '#3db8a5',
    'interactivity': '#6b6ec8',
    'storytelling':  '#d45a6e',
    'insight':       '#3db880',
  };

  const allTags = Object.keys(colormaps).filter(t => t !== STAFF_PICK);
  const filtered = activeTag ? data.filter(row => row.tags.map(t => t.trim()).includes(activeTag)) : data;

  return (
    <div style={{ width: '92%', maxWidth: '1300px', margin: '1rem auto' }}>
      <style>{`
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
        }
        .gallery-card {
          border: 1px solid #e2e2e2;
          border-radius: 10px;
          padding: 14px 14px 48px 14px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.07);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          position: relative;
          cursor: pointer;
          background: #fff;
          min-width: 0;
        }
        .gallery-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.13);
        }
        .gallery-card img {
          width: 100%;
          height: 190px;
          object-fit: cover;
          border-radius: 6px;
          display: block;
        }
        @media (max-width: 480px) {
          .gallery-grid {
            grid-template-columns: 1fr;
          }
          .gallery-card img {
            height: 200px;
          }
        }
      `}</style>

      <p style={{ fontSize: '38px', fontWeight: '500' }}>Final Project Gallery</p>
      <p style={{ marginTop: '1rem', marginBottom: '2rem' }}>
        This gallery showcases the final projects from <a href="https://fumeng-yang.github.io/CMSC471/" target="_blank">CMSC471: Introduction to Information Visualization</a> at the University of Maryland, College Park, taught in <strong>Spring 2026</strong> by <a href="https://www.fmyang.com/">Fumeng Yang</a> with teaching assistants <a href="https://lebretou.github.io/">Zhongzheng Xu</a> and <a href="https://taehyun.me/">Taehyun Yang</a>.
        <br /><br />
        We invite you to browse these <strong>23</strong> projects that reflect each team's interests, data choices, and design decisions. Click on any card to see the live project!
      </p>

      {/* Tag filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
        <button
          onClick={() => setActiveTag(null)}
          style={{
            padding: '5px 14px',
            borderRadius: '20px',
            border: '1.5px solid #ccc',
            background: activeTag === null ? '#d8d8d8' : '#fff',
            color: '#555',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.15s ease',
          }}
        >
          all
        </button>
        {allTags.map(tag => (
          <button
            key={tag}
            onClick={() => setActiveTag(activeTag === tag ? null : tag)}
            style={{
              padding: '5px 14px',
              borderRadius: '20px',
              border: `1.5px solid ${colormaps[tag]}`,
              background: activeTag === tag ? colormaps[tag] : colormaps[tag] + '18',
              color: activeTag === tag ? '#fff' : colormaps[tag],
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.15s ease',
            }}
          >
            #{tag}
          </button>
        ))}
        <button
          onClick={() => setActiveTag(activeTag === STAFF_PICK ? null : STAFF_PICK)}
          style={{
            padding: '5px 14px',
            borderRadius: '20px',
            border: '1.5px solid #c9820a',
            background: activeTag === STAFF_PICK ? 'linear-gradient(135deg, #f5c842, #c9820a)' : 'rgba(245,200,66,0.15)',
            color: activeTag === STAFF_PICK ? '#fff' : '#a06a08',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '700',
            letterSpacing: '0.02em',
            transition: 'all 0.15s ease',
            boxShadow: activeTag === STAFF_PICK ? '0 2px 8px rgba(180,120,0,0.35)' : 'none',
          }}
        >
          ★ staff pick
        </button>
      </div>

      <div className="gallery-grid">
        {filtered.map((row, idx) => (
          <div
            key={idx}
            className="gallery-card"
            onClick={() => {
              if (row.link) window.open(row.link, '_blank', 'noopener,noreferrer');
            }}
          >
            <img src={`/CMSC471/FP/${row.ID}.png`} alt={row.title} />
            <p style={{ fontSize: '17px', marginTop: '0.85rem', fontWeight: '500', marginBottom: '3px' }}>{row.title}</p>
            <p style={{ color: '#777', marginTop: '0', marginBottom: '0', fontSize: '14px' }}>{row.team}</p>
            {row.title.includes('Fourth and Forecast') && (
              <p style={{ color: '#f57842', fontSize: '12px', marginTop: '2px' }}>(takes a few minutes to warm up)</p>
            )}
            <span style={{ position: 'absolute', bottom: '12px', left: '14px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {row.tags.map((tag, o) => {
                const t = tag.trim();
                if (t === STAFF_PICK) return null;
                return (
                  <span
                    key={o}
                    className='tag'
                    style={{ color: colormaps[t], backgroundColor: colormaps[t] + '18', borderRadius: '4px', padding: '1px 6px', fontSize: '12px' }}
                  >
                    #{t}
                  </span>
                );
              })}
            </span>
            {row.tags.map(t => t.trim()).includes(STAFF_PICK) && (
              <span style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'linear-gradient(135deg, #f5c842, #c9820a)',
                color: '#fff',
                borderRadius: '20px',
                padding: '3px 11px',
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '0.02em',
                boxShadow: '0 2px 8px rgba(180,120,0,0.45)',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
              }}>
                ★ staff pick
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
